import {
  BLANK_VALUE,
  formatMetric,
} from "portal/components/measurement/formatters";
import { BlockResponse } from "protos/portal/spatial";
import { CarbonMeasurementSystem, CarbonUnit } from "./units/units";
import {
  ConclusionType,
  conclusionTypeFromJSON,
  HWMetric,
  SpatialMetricBlock,
} from "protos/metrics/metrics";
import { CycleSlot, getSpatialMetricName, SpatialMetric } from "./metrics";
import { FillLayer } from "mapbox-gl";
import {
  getDecimalPlacesForCycleSlot,
  getSavedUnit,
} from "../components/measurement/numericUnits";
import { i18n as I18n, ParseKeys, TFunction } from "i18next";
import { isUndefined, isValidNumber } from "./identity";
import { JigsawSpatialMetricBlock } from "protos/portal/jigsaw";
import { percentile } from "./math";
import { point, Properties, Units } from "@turf/helpers";
import Color from "color";
import distance from "@turf/distance";

type OriginalJigsawBlock = {
  type: "jigsaw";
  block: JigsawSpatialMetricBlock;
};

type OriginalOldBlock = {
  type: "old";
  block: BlockResponse;
};

type OriginalBlock = OriginalJigsawBlock | OriginalOldBlock;

export type NormalizedBlock = Pick<
  SpatialMetricBlock,
  "suspicious" | "start" | "end"
> & {
  countable: boolean;
  originalBlock: OriginalBlock;
};

const isCountableHwMetric = (
  hwMetric: Omit<HWMetric, "debugMode"> | undefined
): boolean =>
  (!hwMetric?.lifted &&
    hwMetric?.laserKey &&
    !hwMetric.estopped &&
    hwMetric.interlock &&
    hwMetric.waterProtect) ??
  false;

export const normalizeOldBlock = (block: BlockResponse): NormalizedBlock => {
  return {
    suspicious: block.block?.suspicious ?? false,
    start: block.block?.start,
    end: block.block?.end,
    countable:
      isActive(block.block) && isCountableHwMetric(block.block?.hwMetric),
    originalBlock: {
      type: "old",
      block,
    },
  };
};

export const normalizeJigsawBlock = (
  block: JigsawSpatialMetricBlock
): NormalizedBlock => {
  return {
    suspicious: false, // jigsaw endpoints do not return suspicious blocks
    start: block.start,
    end: block.end,
    countable: block.countable,
    originalBlock: {
      type: "jigsaw",
      block,
    },
  };
};

export const isWeeding = (block?: SpatialMetricBlock): boolean => {
  let totalCount = 0;
  const armedWeed = block?.weedCount?.conclusionCounts?.armedWeed ?? [];
  for (const [conclusionType, count] of armedWeed.entries()) {
    if (
      ![
        conclusionTypeFromJSON(ConclusionType.NOT_WEEDING),
        conclusionTypeFromJSON(ConclusionType.FLICKER),
        conclusionTypeFromJSON(ConclusionType.FLICKER_ACTIVE),
        conclusionTypeFromJSON(ConclusionType.OBSERVED),
      ].includes(conclusionType)
    ) {
      totalCount += count;
    }
  }

  return totalCount > 0;
};

export const isThinning = (block?: SpatialMetricBlock): boolean => {
  let totalCount = 0;
  const armedCrop = block?.weedCount?.conclusionCounts?.armedCrop ?? [];
  for (const [conclusionType, count] of armedCrop.entries()) {
    if (
      ![
        conclusionTypeFromJSON(ConclusionType.NOT_WEEDING),
        conclusionTypeFromJSON(ConclusionType.FLICKER),
        conclusionTypeFromJSON(ConclusionType.FLICKER_ACTIVE),
        conclusionTypeFromJSON(ConclusionType.OBSERVED),
      ].includes(conclusionType)
    ) {
      totalCount += count;
    }
  }

  return totalCount > 0;
};

export const isActive = (block?: SpatialMetricBlock): boolean =>
  isWeeding(block) || isThinning(block);

export const isCountableBlock = (block?: NormalizedBlock): boolean =>
  block?.countable ?? false;

export const getBlockStyle = (color: string): FillLayer => ({
  id: color,
  type: "fill",
  paint: {
    "fill-color": color,
  },
});

export const getBreakpoints = (
  colors: BlockColors,
  isRelative: boolean = false
): number[] => {
  const absolute =
    colors.absoluteBreakpoints ?? colors.relativeBreakpoints ?? [];
  if (colors.discrete) {
    return absolute;
  }
  const relative =
    colors.relativeBreakpoints ?? colors.absoluteBreakpoints ?? [];
  return isRelative ? relative : absolute;
};

export interface BlockColors {
  absoluteBreakpoints?: number[];
  relativeBreakpoints?: number[];
  colors: Color[];
  labels?: { i18nKey: ParseKeys; captionI18nKey?: ParseKeys }[];
  // Solid swatches. Not a scale, so relative coloring does not apply.
  discrete?: boolean;
}

export interface BlockRange {
  min: number;
  low: number;
  medium: number;
  high: number;
  max: number;
}

export const mixColor = (
  value: number | undefined,
  colors: BlockColors,
  isRelative: boolean = false
): string => {
  if (value === undefined) {
    return "";
  }
  let color: Color | undefined = COLOR_WHITE;
  const breakpoints = getBreakpoints(colors, isRelative);
  const lowestBreakpoint = breakpoints[0];
  const highestBreakpoint = breakpoints.at(-1);
  if (isUndefined(lowestBreakpoint) || isUndefined(highestBreakpoint)) {
    return color.toString();
  }
  if (colors.discrete) {
    const stop = colors.colors[breakpoints.indexOf(value)];
    return (stop ?? COLOR_WHITE).toString();
  }
  for (const [index, low] of breakpoints.entries()) {
    // value is at or below lowest breakpoint
    if (value <= lowestBreakpoint) {
      color = colors.colors[0];
      break;
    }
    // value is at or above highest breakpoint
    if (value >= highestBreakpoint) {
      color = colors.colors.at(-1);
      break;
    }
    const high = breakpoints[index + 1];
    if (isUndefined(high)) {
      break;
    }
    if (low <= value && value <= high) {
      const lowColor = colors.colors[index];
      const highColor = colors.colors[index + 1];
      if (!lowColor || !highColor) {
        break;
      }
      const percent = (value - low) / (high - low);
      color = lowColor.mix(highColor, percent);
      break;
    }
  }
  return color ? color.toString() : "";
};

export const COLOR_BLACK = Color.rgb(0, 0, 0);
export const COLOR_BLUE = Color.rgb(71, 119, 247);
export const COLOR_BROWN = Color.rgb(71, 44, 13);
export const COLOR_GREEN = Color.rgb(71, 179, 89);
export const COLOR_ORANGE = Color.rgb(212, 101, 53);
export const COLOR_PINK = Color.rgb(190, 37, 182);
export const COLOR_RED = Color.rgb(202, 44, 42);
export const COLOR_WHITE = Color.rgb(255, 255, 255).alpha(0.25);
export const COLOR_YELLOW = Color.rgb(229, 178, 0);

export const getProperties = (
  t: TFunction,
  i18n: I18n,
  measurementSystem: CarbonMeasurementSystem,
  block: NormalizedBlock,
  metric: SpatialMetric,
  cycleSlots: Record<CycleSlot, CarbonUnit>
): Properties => {
  const properties: Properties = {};

  for (const popupMetric of metric.popup ?? []) {
    const baseLabel = getSpatialMetricName(t, popupMetric);
    // two categories can resolve to the same name (e.g. a custom category
    // named after a built-in one); suffix duplicates so neither row is lost
    let label = baseLabel;
    for (let ordinal = 2; label in properties; ordinal++) {
      label = `${baseLabel} (${ordinal})`;
    }
    const toUnits =
      "units" in popupMetric
        ? getSavedUnit(
            measurementSystem,
            cycleSlots,
            popupMetric.cycleSlot,
            popupMetric.units
          ).unit
        : undefined;
    const value =
      block.originalBlock.type === "jigsaw"
        ? popupMetric.getJigsawValue(block.originalBlock.block)
        : popupMetric.getValue(block.originalBlock.block);
    properties[label] =
      popupMetric.type === "discrete"
        ? formatDiscreteGrade(t, popupMetric, value)
        : formatMetric(t, i18n, measurementSystem, popupMetric, value, {
            decimalPlaces: getDecimalPlacesForCycleSlot(
              popupMetric.cycleSlot,
              toUnits
            ),
            toUnits,
          }).toString();
  }

  return properties;
};

// Discrete layers store the stop value. The hover shows that stop's label.
const formatDiscreteGrade = (
  t: TFunction,
  metric: SpatialMetric,
  value: unknown
): string => {
  const index =
    typeof value === "number"
      ? metric.colors.absoluteBreakpoints?.indexOf(value)
      : undefined;
  const label =
    index !== undefined && index >= 0
      ? metric.colors.labels?.[index]
      : undefined;
  return label ? t(label.i18nKey) : BLANK_VALUE;
};

export const getWidth = (
  block: SpatialMetricBlock | JigsawSpatialMetricBlock | undefined,
  units: Units
): number | undefined => {
  if (isUndefined(block)) {
    return;
  }
  const startLeftY = block.startLeft?.longitude;
  const startLeftX = block.startLeft?.latitude;
  const startRightY = block.startRight?.longitude;
  const startRightX = block.startRight?.latitude;
  const endLeftY = block.endLeft?.longitude;
  const endLeftX = block.endLeft?.latitude;
  const endRightY = block.endRight?.longitude;
  const endRightX = block.endRight?.latitude;
  if (
    isUndefined(startLeftY) ||
    isUndefined(startLeftX) ||
    isUndefined(startRightY) ||
    isUndefined(startRightX) ||
    isUndefined(endLeftY) ||
    isUndefined(endLeftX) ||
    isUndefined(endRightY) ||
    isUndefined(endRightX)
  ) {
    return;
  }
  const startWidth = distance(
    point([startLeftY, startLeftX]),
    point([startRightY, startRightX]),
    { units }
  );
  const endWidth = distance(
    point([endLeftY, endLeftX]),
    point([endRightY, endRightX]),
    { units }
  );
  return (startWidth + endWidth) / 2;
};

export const getLength = (
  block: BlockResponse | undefined,
  units: Units
): number | undefined => {
  if (isUndefined(block)) {
    return;
  }
  const startY = block.block?.start?.longitude;
  const startX = block.block?.start?.latitude;
  const endY = block.block?.end?.longitude;
  const endX = block.block?.end?.latitude;
  if (
    isUndefined(startY) ||
    isUndefined(startX) ||
    isUndefined(endY) ||
    isUndefined(endX)
  ) {
    return;
  }
  return distance(point([startY, startX]), point([endY, endX]), {
    units,
  });
};

export const getBlockRange = (
  blocks: NormalizedBlock[] | undefined,
  metric: SpatialMetric
): BlockRange | undefined => {
  if (isUndefined(blocks)) {
    return;
  }
  const values = getBlockValues(blocks, metric);
  if (values === undefined) {
    return;
  }
  if (values.length === 0) {
    return;
  }
  const min = values[0];
  const low = percentile(values, 10);
  const medium = percentile(values, 50);
  const high = percentile(values, 90);
  const max = values.at(-1);
  if (
    isUndefined(min) ||
    isUndefined(low) ||
    isUndefined(medium) ||
    isUndefined(high) ||
    isUndefined(max)
  ) {
    return;
  }
  return {
    min,
    low,
    medium,
    high,
    max,
  };
};

export const aggregateConclusionCounts = (
  conclusionCounts: number[],
  conclusionTypes: ConclusionType[]
): number => {
  let total = 0;
  for (const conclusionType of conclusionTypes) {
    total += conclusionCounts[conclusionType] ?? 0;
  }
  return total;
};

function getBlockValues(
  blocks: NormalizedBlock[],
  metric: SpatialMetric
): number[] | undefined {
  return blocks
    .filter((block) => isCountableBlock(block))
    .map((block) => {
      if (block.originalBlock.type === "jigsaw") {
        return metric.getJigsawValue(block.originalBlock.block);
      } else {
        return metric.getValue(block.originalBlock.block);
      }
    })
    .filter((value) => isValidNumber(value))
    .sort((a, b) => a - b);
}
